/**
 * Nadeln eines Eintrags auf den Projektplaenen (docs/planverortung-vertrag.md).
 *
 * Ein Mangel kann an MEHREREN Stellen verortet sein, auch auf mehreren
 * Plaenen (Betreiber 28.09.2026). Gespeichert wird weiter in den zwei alten
 * Feldern, damit Altstaende ohne Umbau lesbar bleiben:
 *
 *   planCoordinates  Array {x, y, planId, sensor?} in Plan-Prozent.
 *                    Altstand: Array ohne planId oder ein Einzelobjekt —
 *                    dann gilt der Plan des Eintrags.
 *   planId           Plan der ERSTEN Nadel.
 *
 * Die Sensor-Nadel (Live-Standort, Auto-Ort, Ortsvorschlag) traegt
 * `sensor: true` und steht immer vorn; an ihr haengt der Blickpfeil aus
 * `verortung`. Sie ist AUSDRUECKLICH markiert und nicht „Index 0, sobald
 * `verortung` gesetzt ist“: `verortung` steht auch ohne Nadel (nur das
 * Geschoss), und eine Hand-Nadel an Index 0 galt sonst als Sensor-Nadel —
 * „Live übernehmen“ hat sie dann ersetzt (Codex-Review 28.09., P1).
 *
 * Alle Ansichten, der PDF-Plananhang und der Speicherweg lesen und aendern die
 * Nadeln NUR hierueber — sonst zeigt der Bildschirm etwas anderes als der
 * Ausdruck (siehe selectItemsOnPlan).
 */
import type { InspectionItem, PlanCoordinates } from '../types';

/** Eine Nadel, normalisiert: Plan immer gesetzt. */
export interface PlanPin {
    x: number;
    y: number;
    planId: string;
    /** Sensor-Nadel (Blickpfeil). Fehlt = von Hand. */
    sensor?: boolean;
}

/** Nadel mit ihrem Index in `pinsVon` (zum Entfernen). */
export type IndizierterPin = PlanPin & { index: number };

export type PinQuelle = Pick<InspectionItem, 'planId' | 'planCoordinates'> & Partial<Pick<InspectionItem, 'verortung'>>;

/** Die zwei Felder am Eintrag, die die Nadeln tragen. */
export interface PinFelder {
    planId: string | undefined;
    planCoordinates: PlanCoordinates[] | undefined;
}

const istZahl = (wert: unknown): wert is number => typeof wert === 'number' && Number.isFinite(wert);

const rohListe = (roh: unknown): unknown[] => {
    if (Array.isArray(roh)) return roh;
    if (roh && typeof roh === 'object') return [roh];
    return [];
};

/**
 * Altstand vor der Markierung (Sitzungen/Schnappschuesse bis 28.09.2026):
 * keine Nadel traegt einen eigenen Plan, keine ist markiert, und `verortung`
 * stammt von einem Planpunkt → damals galt „Index 0 = Sensor-Nadel“. Erkannt
 * an Blick ODER Genauigkeit: jeder alte Weg mit Planpunkt (Live, Auto-Ort,
 * Geschoss-Knopf mit Live-Punkt, Ortsvorschlag mit Pin) schrieb die
 * Genauigkeit als Zahl, die reinen Geschoss-Wege setzten beide auf null
 * (Codex-Review 28.09., Runde 2). Neue Staende schreiben den Plan an jede
 * Nadel und fallen hier nie hinein.
 */
const altstandSensorVorn = (roh: unknown[], item: PinQuelle): boolean =>
    (istZahl(item.verortung?.blickGradPlan) || istZahl(item.verortung?.genauigkeitM))
    && roh.every(c => !c || typeof c !== 'object'
        || (typeof (c as PlanCoordinates).planId !== 'string' && (c as PlanCoordinates).sensor !== true));

/**
 * Alle Nadeln eines Eintrags in gespeicherter Reihenfolge. Nadeln ohne
 * gueltige Zahl oder ohne Plan (weder an der Nadel noch am Eintrag) fallen
 * weg. Hoechstens eine Nadel gilt als Sensor-Nadel (die erste markierte).
 */
export function pinsVon(item: PinQuelle): PlanPin[] {
    const eintragPlan = typeof item.planId === 'string' && item.planId ? item.planId : undefined;
    const roh = rohListe(item.planCoordinates);
    const altstand = altstandSensorVorn(roh, item);
    const pins: PlanPin[] = [];
    let sensorVergeben = false;
    for (const eintrag of roh) {
        if (!eintrag || typeof eintrag !== 'object') continue;
        const c = eintrag as Partial<PlanCoordinates>;
        if (!istZahl(c.x) || !istZahl(c.y)) continue;
        const planId = typeof c.planId === 'string' && c.planId ? c.planId : eintragPlan;
        if (!planId) continue;
        const sensor = !sensorVergeben && (c.sensor === true || (altstand && pins.length === 0));
        if (sensor) sensorVergeben = true;
        pins.push(sensor ? { x: c.x, y: c.y, planId, sensor: true } : { x: c.x, y: c.y, planId });
    }
    return pins;
}

/** Nadeln auf einem Plan, mit ihrem Index in `pinsVon`. */
export function pinsAufPlan(item: PinQuelle, planId: string | null | undefined): IndizierterPin[] {
    if (!planId) return [];
    return pinsVon(item)
        .map((pin, index) => ({ ...pin, index }))
        .filter(pin => pin.planId === planId);
}

/** Plaene mit Nadeln, in der Reihenfolge ihres ersten Auftretens. */
export function planIdsVon(item: PinQuelle): string[] {
    const ids: string[] = [];
    for (const pin of pinsVon(item)) if (!ids.includes(pin.planId)) ids.push(pin.planId);
    return ids;
}

export function hatPins(item: PinQuelle): boolean {
    return pinsVon(item).length > 0;
}

/**
 * Felder fuer den Eintrag aus einer Nadelliste. Jede Nadel traegt ihren Plan
 * ausdruecklich (und `sensor: true`, wenn sie die Sensor-Nadel ist);
 * `planId` = Plan der ersten Nadel. Leer → beide undefined (der Speicherweg
 * schreibt daraus NULL).
 */
export function pinFelder(pins: readonly PlanPin[]): PinFelder {
    if (pins.length === 0) return { planId: undefined, planCoordinates: undefined };
    return {
        planId: pins[0].planId,
        planCoordinates: pins.map(p => (p.sensor
            ? { x: p.x, y: p.y, planId: p.planId, sensor: true }
            : { x: p.x, y: p.y, planId: p.planId })),
    };
}

/** Hand-Nadel hinten anhaengen. Eine Sensor-Nadel bleibt vorn. */
export function pinHinzufuegen(item: PinQuelle, pin: PlanPin): PinFelder {
    return pinFelder([...pinsVon(item), { x: pin.x, y: pin.y, planId: pin.planId }]);
}

/**
 * Nadel entfernen. Faellt die Sensor-Nadel weg, gehoert der Blick aus
 * `verortung` zu keiner Nadel mehr → `verortung` wird undefined.
 */
export function pinEntfernen(item: PinQuelle, index: number): PinFelder & { verortung: InspectionItem['verortung'] } {
    const pins = pinsVon(item);
    if (index < 0 || index >= pins.length) return { ...pinFelder(pins), verortung: item.verortung };
    return {
        ...pinFelder(pins.filter((_, i) => i !== index)),
        verortung: pins[index].sensor ? undefined : item.verortung,
    };
}

/**
 * Sensor-Nadel setzen: ersetzt eine vorhandene Sensor-Nadel und steht vorn;
 * Hand-Nadeln bleiben unberuehrt. `verortung` setzt der Aufrufer.
 */
export function sensorPinSetzen(item: PinQuelle, pin: Pick<PlanPin, 'x' | 'y' | 'planId'>): PinFelder {
    const handNadeln = pinsVon(item).filter(p => !p.sensor);
    return pinFelder([{ x: pin.x, y: pin.y, planId: pin.planId, sensor: true }, ...handNadeln]);
}

/** Nur die Sensor-Nadel entfernen (Hand-Nadeln bleiben). */
export function sensorPinEntfernen(item: PinQuelle): PinFelder {
    return pinFelder(pinsVon(item).filter(p => !p.sensor));
}
